import type { Combobox } from '@/global/components/Combobox'
import { inputVariants, rotuloCampoClass } from '@/global/components/ui/input'
import { Label } from '@/global/components/ui/label'
import { cn } from '@/lib/utils'
import { RefreshCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

/** Alterar classes duration-[] */
const DURACAO_VALOR = 300
const DURACAO_ICONE = 420

type VarianteSwitchSelect = 'filled' | 'outlined'

/** `outlined` reaproveita a caixa e o rótulo do {@link Combobox} para os dois seguirem juntos. */
const CAIXA_POR_VARIANTE: Record<VarianteSwitchSelect, string> = {
    filled: cn(
        'rounded-sm bg-muted/80 rounded-b-none transition-colors',
        'border-0 border-b border-b-foreground/40',
        'hover:bg-accent hover:border-b-foreground',
        'focus-visible:border-b-primary focus-visible:shadow-[inset_0_-2px_0_0_var(--primary)]',
        'disabled:opacity-disabled disabled:hover:bg-muted/80 disabled:hover:border-b-foreground/40',
    ),
    outlined: inputVariants({
        variant: 'outlined',
        size: 'small',
        comRotulo: true,
    }),
}

const ROTULO_POR_VARIANTE: Record<VarianteSwitchSelect, string> = {
    filled: 'text-xs leading-[17.25px] font-normal text-muted-foreground transition-colors group-hover:text-primary group-focus-visible:text-primary',
    outlined: cn(rotuloCampoClass),
}

const ICONE_POR_VARIANTE: Record<VarianteSwitchSelect, string> = {
    filled: 'group-hover:text-primary group-focus-visible:text-primary',
    outlined: '',
}

export type SwitchSelectOption<T extends string> = {
    value: T
    label: string
}

/** Mesma altura do `SelectField`: 56px (`medium`, campo de formulário) ou 48px (`small`). */
const ALTURA_POR_TAMANHO = { medium: 'h-14', small: 'h-12' } as const

type SwitchSelectProps<T extends string> = {
    label: string
    value: T
    options: readonly [SwitchSelectOption<T>, SwitchSelectOption<T>]
    onChange: (value: T) => void
    disabled?: boolean
    helperText?: string
    className?: string
    variant?: VarianteSwitchSelect
    size?: keyof typeof ALTURA_POR_TAMANHO
    /**
     * Não reserva a linha auxiliar abaixo do campo. Padrão `true` (campo solto);
     * o wrapper de formulário passa `false` para alinhar com os outros `Form*`.
     */
    unHelperText?: boolean
}

/**
 * Campo de dois valores que alterna ao clique, no lugar de abrir uma lista.
 * Herda a caixa, o label e o tamanho do Select, mas troca o chevron por uma
 * seta circular que gira a cada troca e desliza o valor verticalmente.
 *
 * Usar só com exatamente dois valores mutuamente exclusivos e troca barata.
 * Com três ou mais, ou quando a troca dispara recarga cara, manter o Select.
 */
export function SwitchSelect<T extends string>({
    label,
    value,
    options,
    onChange,
    disabled,
    helperText,
    className,
    variant = 'filled',
    size = 'small',
    unHelperText = true,
}: SwitchSelectProps<T>) {
    const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    const [saindo, setSaindo] = useState<{ label: string; avanca: boolean }>()
    const [rotacao, setRotacao] = useState(0)

    useEffect(() => () => clearTimeout(timeout.current), [])

    const indice = options.findIndex(option => option.value === value)
    const atual = options[indice] ?? options[0]
    const proximo = options[indice === 0 ? 1 : 0]

    function alternar() {
        if (disabled) return

        const avanca = indice === 0

        setSaindo({ label: atual.label, avanca })
        setRotacao(atual => atual + (avanca ? 180 : -180))
        onChange(proximo.value)

        clearTimeout(timeout.current)
        timeout.current = setTimeout(() => setSaindo(undefined), DURACAO_VALOR)
    }

    return (
        <div className={cn('flex flex-col gap-1 w-[200px]', className)}>
            <button
                type="button"
                aria-label={label}
                disabled={disabled}
                data-disabled={disabled || undefined}
                onClick={alternar}
                onKeyDown={event => {
                    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')
                        return
                    event.preventDefault()
                    alternar()
                }}
                onKeyDownCapture={event => event.stopPropagation()}
                className={cn(
                    CAIXA_POR_VARIANTE[variant],
                    ALTURA_POR_TAMANHO[size],
                    'group flex w-full flex-row cursor-pointer items-center justify-between gap-2 px-3 text-left',
                    'outline-none disabled:cursor-not-allowed',
                )}
            >
                <span className="flex min-w-0 flex-col select-none">
                    <Label
                        className={cn(
                            'pointer-events-none',
                            ROTULO_POR_VARIANTE[variant],
                        )}
                    >
                        {label}
                    </Label>
                    <span className="relative block h-[23px] overflow-hidden">
                        <span
                            key={atual.value}
                            className={cn(
                                'block truncate text-base leading-[23px] text-foreground',
                                saindo && 'animate-in fade-in duration-300',
                                saindo?.avanca && 'slide-in-from-bottom-3',
                                saindo?.avanca === false &&
                                    'slide-in-from-top-3',
                            )}
                        >
                            {atual.label}
                        </span>
                        {saindo && (
                            <span
                                aria-hidden
                                className={cn(
                                    'absolute inset-0 block truncate text-base leading-[23px] text-foreground',
                                    'animate-out fade-out fill-mode-forwards duration-300',
                                    saindo.avanca
                                        ? 'slide-out-to-top-3'
                                        : 'slide-out-to-bottom-3',
                                )}
                            >
                                {saindo.label}
                            </span>
                        )}
                    </span>
                </span>
                <RefreshCw
                    aria-hidden
                    className={cn(
                        'size-4 shrink-0 text-muted-foreground transition-transform ease-out',
                        ICONE_POR_VARIANTE[variant],
                    )}
                    style={{
                        transform: `rotate(${rotacao}deg)`,
                        transitionDuration: `${DURACAO_ICONE}ms`,
                    }}
                />
            </button>
            {(helperText || !unHelperText) && (
                <span className="px-3 text-xs text-muted-foreground">
                    {helperText ?? ' '}
                </span>
            )}
        </div>
    )
}

export default SwitchSelect
